import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronUp, Package, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { toast } from '@/components/ui/toast-store';
import { removeInvalid, removeMissing, useDependencyErrors } from '@/features/file-tree/dependency-error-store';
import { fetchDependencies, updateDependencies } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { validateDependencyName, validateDependencyVersion } from '@shared/validation';

interface DependencyPanelProperties {
	projectId: string;
	collapsed?: boolean;
	onToggle?: () => void;
	className?: string;
}

interface DependencyEntry {
	name: string;
	version: string;
}

function DependencyPanel({ projectId, collapsed = false, onToggle, className }: DependencyPanelProperties) {
	const queryClient = useQueryClient();
	const [isAdding, setIsAdding] = useState(false);
	const [editingName, setEditingName] = useState<string | undefined>();
	const [addInput, setAddInput] = useState('');
	const [editVersion, setEditVersion] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [addError, setAddError] = useState<string | undefined>();
	const [editError, setEditError] = useState<string | undefined>();
	const nameInputReference = useRef<HTMLInputElement>(null);
	const editInputReference = useRef<HTMLInputElement>(null);
	const editCommittedReference = useRef(false);

	// Read dependency errors from the global store (active even when this component is unmounted)
	const { missing: missingDependencies, invalid: invalidDependencies } = useDependencyErrors();

	// Load dependencies from package.json via React Query
	const depsQuery = useQuery({
		queryKey: ['project-deps', projectId],
		queryFn: () => fetchDependencies(projectId),
		staleTime: 1000 * 30,
	});
	const dependencies = useMemo(() => {
		const dependencyRecord = depsQuery.data ?? {};
		return Object.entries(dependencyRecord)
			.map(([name, version]) => ({ name, version: String(version) }))
			.toSorted((a, b) => a.name.localeCompare(b.name));
	}, [depsQuery.data]);

	const saveDependencies = useCallback(
		async (entries: DependencyEntry[]) => {
			const queryKey = ['project-deps', projectId];
			const previousData = queryClient.getQueryData(queryKey);
			// Optimistic update: write new dependencies into the query cache
			const newRecord: Record<string, string> = {};
			for (const entry of entries) {
				newRecord[entry.name] = entry.version;
			}
			queryClient.setQueryData(queryKey, () => newRecord);
			setIsLoading(true);
			try {
				await updateDependencies(projectId, newRecord);
			} catch {
				queryClient.setQueryData(queryKey, previousData);
				toast.error('Failed to update dependencies');
			} finally {
				setIsLoading(false);
			}
		},
		[projectId, queryClient],
	);

	const parseAddInput = useCallback((input: string): { name: string; version: string } | undefined => {
		const trimmed = input.trim();
		if (!trimmed) return undefined;
		// Support scoped packages: @scope/name@version
		const atIndex = trimmed.startsWith('@') ? trimmed.indexOf('@', 1) : trimmed.indexOf('@');
		if (atIndex > 0) {
			return { name: trimmed.slice(0, atIndex), version: trimmed.slice(atIndex + 1) || '*' };
		}
		return { name: trimmed, version: '*' };
	}, []);

	const handleAdd = useCallback(async () => {
		const parsed = parseAddInput(addInput);
		if (!parsed) return;

		// Validate name
		const nameError = validateDependencyName(parsed.name);
		if (nameError) {
			setAddError(nameError);
			return;
		}

		// Validate version
		const versionError = validateDependencyVersion(parsed.version);
		if (versionError) {
			setAddError(versionError);
			return;
		}

		if (dependencies.some((d) => d.name === parsed.name)) {
			setAddError(`"${parsed.name}" is already added. Edit its version instead.`);
			return;
		}

		const updated = [...dependencies, parsed];
		setIsAdding(false);
		setAddInput('');
		setAddError(undefined);
		removeMissing(parsed.name);
		removeInvalid(parsed.name);
		await saveDependencies(updated);
	}, [addInput, parseAddInput, dependencies, saveDependencies]);

	const handleAddMissing = useCallback(
		async (name: string) => {
			if (dependencies.some((d) => d.name === name)) {
				removeMissing(name);
				return;
			}
			const updated = [...dependencies, { name, version: '*' }];
			removeMissing(name);
			await saveDependencies(updated);
		},
		[dependencies, saveDependencies],
	);

	const handleRemove = useCallback(
		async (name: string) => {
			const updated = dependencies.filter((d) => d.name !== name);
			await saveDependencies(updated);
		},
		[dependencies, saveDependencies],
	);

	const handleEditStart = useCallback(
		(name: string) => {
			const entry = dependencies.find((d) => d.name === name);
			if (entry) {
				editCommittedReference.current = false;
				setEditingName(name);
				setEditVersion(entry.version);
				requestAnimationFrame(() => {
					editInputReference.current?.focus();
					editInputReference.current?.select();
				});
			}
		},
		[dependencies],
	);

	const handleEditSave = useCallback(async () => {
		if (!editingName || editCommittedReference.current) return;
		const version = editVersion.trim() || '*';

		// Validate version
		const versionError = validateDependencyVersion(version);
		if (versionError) {
			setEditError(versionError);
			return;
		}

		editCommittedReference.current = true;

		const updated = dependencies.map((d) => (d.name === editingName ? { ...d, version } : d));
		setEditingName(undefined);
		setEditVersion('');
		setEditError(undefined);
		removeInvalid(editingName);
		await saveDependencies(updated);
	}, [editingName, editVersion, dependencies, saveDependencies]);

	const handleAddKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === 'Enter') {
				void handleAdd();
			} else if (event.key === 'Escape') {
				setIsAdding(false);
				setAddInput('');
				setAddError(undefined);
			}
		},
		[handleAdd],
	);

	const handleEditKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === 'Enter') {
				event.stopPropagation();
				void handleEditSave();
			} else if (event.key === 'Escape') {
				event.stopPropagation();
				setEditingName(undefined);
				setEditVersion('');
				setEditError(undefined);
			}
		},
		[handleEditSave],
	);

	if (collapsed) {
		return (
			<button
				type="button"
				onClick={onToggle}
				className={cn(
					'flex h-7 w-full shrink-0 cursor-pointer items-center gap-1 px-2 text-2xs',
					'border-t border-border bg-bg-secondary font-medium tracking-wider',
					'text-text-secondary',
					'hover:bg-bg-tertiary hover:text-text-primary',
					className,
				)}
				aria-label="Show dependencies"
			>
				<ChevronUp className="size-3.5" />
				<Package className="size-3" />
				<span className="flex-1 text-left">Dependencies</span>
				<span className="text-3xs text-text-secondary tabular-nums">{dependencies.length}</span>
			</button>
		);
	}

	return (
		<div className={cn('flex flex-col', className)}>
			<button
				type="button"
				onClick={onToggle}
				className={`
					flex h-7 shrink-0 cursor-pointer items-center gap-1 px-2 text-2xs
					font-medium tracking-wider text-text-secondary
					hover:text-text-primary
				`}
			>
				<ChevronDown className="size-3.5" />
				<Package className="size-3" />
				<span className="flex-1 text-left">Dependencies</span>
				<span className="text-3xs text-text-secondary tabular-nums">{dependencies.length}</span>
			</button>

			<div className="flex flex-col gap-0.5 px-1 pb-1.5">
				{isAdding ? (
					<div className="flex flex-col gap-0.5">
						<input
							ref={nameInputReference}
							type="text"
							value={addInput}
							onChange={(event) => {
								setAddInput(event.target.value);
								setAddError(undefined);
							}}
							onKeyDown={handleAddKeyDown}
							onBlur={() => {
								if (!addInput.trim()) {
									setIsAdding(false);
									setAddInput('');
									setAddError(undefined);
								}
							}}
							placeholder="name or name@version"
							aria-invalid={addError !== undefined}
							className={cn(
								`
									h-6 rounded-sm border bg-bg-primary px-1.5 text-2xs text-text-primary
									outline-none
								`,
								addError ? 'border-error' : 'border-accent',
							)}
						/>
						{addError && (
							<span role="alert" className="px-1 text-3xs/tight text-error">
								{addError}
							</span>
						)}
					</div>
				) : (
					<button
						type="button"
						onClick={() => {
							setIsAdding(true);
							requestAnimationFrame(() => nameInputReference.current?.focus());
						}}
						disabled={isLoading}
						className={`
							flex h-6 cursor-pointer items-center gap-1.5 rounded-sm px-1.5 text-2xs
							text-text-secondary
							hover:bg-bg-tertiary hover:text-text-primary
							disabled:opacity-50
						`}
					>
						<Plus className="size-3" />
						<span>Add dependency</span>
					</button>
				)}

				{missingDependencies.size > 0 && (
					<div
						className={`
							flex flex-col gap-0.5 rounded-sm border border-warning/30 bg-warning/5
							p-1
						`}
					>
						<span className="flex items-center gap-1 px-0.5 text-3xs text-warning">
							<AlertTriangle className="size-3" />
							Missing
						</span>
						{[...missingDependencies].map((name) => (
							<button
								key={name}
								type="button"
								onClick={() => void handleAddMissing(name)}
								disabled={isLoading}
								className={`
									flex h-5 cursor-pointer items-center gap-1 rounded-sm px-1 text-2xs
									text-warning
									hover:bg-warning/10
									disabled:opacity-50
								`}
							>
								<Plus className="size-2.5" />
								<span className="flex-1 truncate text-left">{name}</span>
								<span className="text-3xs opacity-60">@*</span>
							</button>
						))}
					</div>
				)}

				{dependencies.map((entry, index) => {
					const dependencyError = invalidDependencies.get(entry.name);
					const isInvalid = dependencyError !== undefined;
					return (
						<div key={entry.name} className="flex flex-col">
							<div
								role="option"
								data-local-focus="true"
								tabIndex={index === 0 ? 0 : -1}
								aria-selected={editingName === entry.name}
								aria-invalid={isInvalid}
								onKeyDown={(event) => {
									const row = event.currentTarget;
									switch (event.key) {
										case 'ArrowDown': {
											event.preventDefault();
											const next = row.parentElement?.nextElementSibling?.querySelector('[role="option"]');
											if (next instanceof HTMLElement) next.focus();
											break;
										}
										case 'ArrowUp': {
											event.preventDefault();
											const previous = row.parentElement?.previousElementSibling?.querySelector('[role="option"]');
											if (previous instanceof HTMLElement) previous.focus();
											break;
										}
										case 'Enter':
										case 'F2': {
											event.preventDefault();
											handleEditStart(entry.name);
											break;
										}
										case 'Delete': {
											event.preventDefault();
											void handleRemove(entry.name);
											break;
										}
										default: {
											break;
										}
									}
								}}
								className={cn(
									'group flex h-6 items-center gap-1 rounded-sm px-1.5 text-2xs',
									'hover:bg-bg-tertiary',
									`
										focus-visible:ring-1 focus-visible:ring-accent
										focus-visible:outline-none
									`,
									isInvalid && 'bg-error/5',
								)}
							>
								{editingName === entry.name ? (
									<>
										<span className="min-w-0 shrink truncate text-text-primary">{entry.name}@</span>
										<input
											ref={editInputReference}
											type="text"
											value={editVersion}
											onChange={(event) => {
												setEditVersion(event.target.value);
												setEditError(undefined);
											}}
											onKeyDown={handleEditKeyDown}
											onBlur={() => void handleEditSave()}
											aria-invalid={editError !== undefined}
											className={cn(
												'h-5 min-w-0 flex-1 rounded-sm border bg-bg-primary px-1',
												'text-2xs text-text-primary outline-none',
												editError
													? 'border-error'
													: `
														border-border
														focus:border-accent
													`,
											)}
										/>
									</>
								) : (
									<>
										{isInvalid && <AlertTriangle className="size-3 shrink-0 text-error" />}
										<span
											className={cn('min-w-0 flex-1 truncate', isInvalid ? 'text-error' : 'text-text-primary')}
											onDoubleClick={() => handleEditStart(entry.name)}
										>
											{entry.name}
											<span className={cn(isInvalid ? 'text-error/70' : 'text-text-secondary')}>@{entry.version}</span>
										</span>
										<button
											type="button"
											tabIndex={-1}
											onClick={() => handleEditStart(entry.name)}
											aria-label={`Edit version for ${entry.name}`}
											className={`
												hidden size-4 shrink-0 cursor-pointer items-center justify-center
												rounded-sm text-text-secondary transition-colors
												hover-always:text-text-primary
												group-hover-always:flex
											`}
										>
											<Pencil className="size-2.5" />
										</button>
										<button
											type="button"
											tabIndex={-1}
											onClick={() => void handleRemove(entry.name)}
											aria-label={`Remove ${entry.name}`}
											className={`
												hidden size-4 shrink-0 cursor-pointer items-center justify-center
												rounded-sm text-text-secondary transition-colors
												hover-always:text-error
												group-hover-always:flex
											`}
										>
											<Trash2 className="size-2.5" />
										</button>
									</>
								)}
							</div>
							{editingName === entry.name && editError && (
								<span role="alert" className="px-1.5 text-3xs/tight text-error">
									{editError}
								</span>
							)}
							{isInvalid && editingName !== entry.name && <span className="px-1.5 text-3xs/tight text-error">{dependencyError}</span>}
						</div>
					);
				})}
			</div>
		</div>
	);
}

export { DependencyPanel };
export type { DependencyPanelProperties };
